import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import './SqlAutocomplete.css';

// Oracle SQL Keywords
const SQL_KEYWORDS = [
  // DML
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'EXISTS', 'BETWEEN', 'LIKE', 'IS', 'NULL',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'MERGE',
  // Joins
  'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'CROSS JOIN', 'ON', 'USING',
  // Clauses
  'ORDER BY', 'GROUP BY', 'HAVING', 'DISTINCT', 'AS', 'ASC', 'DESC',
  'UNION', 'UNION ALL', 'INTERSECT', 'MINUS',
  // DDL
  'CREATE', 'TABLE', 'DROP', 'ALTER', 'TRUNCATE', 'RENAME',
  // Oracle-specific
  'ROWNUM', 'ROWID', 'DUAL', 'SYSDATE', 'SYSTIMESTAMP', 'USER', 'USERENV',
  'CONNECT BY', 'START WITH', 'PRIOR', 'LEVEL',
  'FETCH', 'NEXT', 'ROWS', 'ONLY', 'FIRST',
  // Functions context
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'DECODE', 'NVL', 'NVL2', 'COALESCE',
  // Transactions
  'COMMIT', 'ROLLBACK', 'SAVEPOINT',
  // WITH clause
  'WITH', 'RECURSIVE',
];

// Oracle SQL Functions
const ORACLE_FUNCTIONS = [
  // String functions
  'SUBSTR', 'INSTR', 'LENGTH', 'LOWER', 'UPPER', 'INITCAP', 'TRIM', 'LTRIM', 'RTRIM',
  'REPLACE', 'TRANSLATE', 'LPAD', 'RPAD', 'CONCAT', '||',
  // Number functions
  'ROUND', 'TRUNC', 'CEIL', 'FLOOR', 'MOD', 'ABS', 'POWER', 'SQRT', 'SIGN',
  // Date functions
  'TO_CHAR', 'TO_DATE', 'TO_NUMBER', 'TO_TIMESTAMP', 'TO_CLOB', 'TO_BLOB',
  'ADD_MONTHS', 'MONTHS_BETWEEN', 'LAST_DAY', 'NEXT_DAY', 'EXTRACT',
  'TRUNC', 'ROUND', 'SYSDATE', 'SYSTIMESTAMP', 'CURRENT_DATE', 'CURRENT_TIMESTAMP',
  // Aggregate functions
  'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'STDDEV', 'VARIANCE',
  'LISTAGG', 'XMLAGG', 'WM_CONCAT',
  // Analytic functions
  'ROW_NUMBER', 'RANK', 'DENSE_RANK', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE',
  'OVER', 'PARTITION BY', 'RANGE', 'ROWS', 'UNBOUNDED PRECEDING', 'UNBOUNDED FOLLOWING',
  // Conversion functions
  'CAST', 'CONVERT', 'HEXTORAW', 'RAWTOHEX',
  // Null handling
  'NVL', 'NVL2', 'NULLIF', 'COALESCE',
  // Conditional
  'DECODE', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  // Other Oracle functions
  'DUMP', 'GREATEST', 'LEAST', 'SYS_GUID', 'SYS_CONTEXT',
];

const TABLE_CONTEXT_KEYWORDS = ['FROM', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'UPDATE', 'INTO', 'TABLE'];
const COLUMN_CONTEXT_KEYWORDS = ['SELECT', 'WHERE', 'HAVING', 'ORDER BY', 'GROUP BY', 'SET', 'VALUES', 'ON', 'AND', 'OR'];

const SqlAutocomplete = forwardRef(({ query, onQueryChange, tables = [], packages = [], columns = [], cursorPosition, textareaRef }, ref) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [suggestionPosition, setSuggestionPosition] = useState({ top: 0, left: 0 });
  const suggestionsRef = useRef(null);

  const getCurrentWord = () => {
    if (query == null || cursorPosition == null) return { word: '', start: cursorPosition ?? 0 };
    const textBefore = query.substring(0, cursorPosition);
    const match = textBefore.match(/([\w.*]+)$/);
    const word = match ? match[1] : '';
    const start = cursorPosition - word.length;
    return { word, start };
  };

  const isAfterColumnContextKeyword = () => {
    if (!query || cursorPosition == null) return false;
    const textBefore = query.substring(0, cursorPosition);
    const upperBefore = textBefore.toUpperCase();
    for (const kw of COLUMN_CONTEXT_KEYWORDS) {
      const idx = upperBefore.lastIndexOf(kw);
      if (idx === -1) continue;
      const after = textBefore.substring(idx + kw.length).trim();
      if (after.length > 0) return true;
    }
    return false;
  };

  const isAfterTableOrAliasDot = () => {
    if (!query || cursorPosition == null) return false;
    const textBefore = query.substring(0, cursorPosition);
    const match = textBefore.match(/([\w.]+)\.\s*$/);
    return !!match;
  };

  const isAfterTableContextKeyword = () => {
    if (!query || cursorPosition == null) return false;
    const textBefore = query.substring(0, cursorPosition);
    const tokens = textBefore.split(/\s+/).filter(Boolean);
    if (tokens.length < 2) return false;
    const lastToken = (tokens[tokens.length - 1] || '').toUpperCase();
    const prevToken = (tokens[tokens.length - 2] || '').toUpperCase();
    if (TABLE_CONTEXT_KEYWORDS.includes(lastToken)) return true;
    if ((lastToken === 'JOIN' || lastToken === 'INNER' || lastToken === 'LEFT' || lastToken === 'RIGHT' || lastToken === 'FULL' || lastToken === 'CROSS') && TABLE_CONTEXT_KEYWORDS.includes(prevToken)) return true;
    return TABLE_CONTEXT_KEYWORDS.includes(prevToken);
  };

  const insertSuggestion = (text, item) => {
    const { start } = getCurrentWord();
    if (start == null) return;
    const before = query.substring(0, start);
    const after = query.substring(cursorPosition);
    
    // Handle function insertion - if it ends with '(', place cursor inside parentheses
    let insertText = text;
    let newCursor = start + text.length;
    
    if (item?.type === 'function' && text.endsWith('(')) {
      // For functions, insert with cursor positioned inside parentheses
      insertText = text;
      newCursor = start + text.length - 1; // Position before closing paren
    }
    
    const newText = before + insertText + after;
    onQueryChange(newText);
    setShowSuggestions(false);
    setTimeout(() => {
      if (textareaRef?.current) {
        textareaRef.current.setSelectionRange(newCursor, newCursor);
        textareaRef.current.focus();
      }
    }, 0);
  };

  useImperativeHandle(ref, () => ({
    handleKeyDown: (e) => {
      if (!showSuggestions || suggestions.length === 0) return false;
      
      // Dismiss on Escape
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return true;
      }
      
      // Dismiss on characters that indicate user is continuing to type
      const dismissKeys = [' ', 'Enter', 'Tab', ',', ';', '(', ')', '=', '<', '>'];
      if (dismissKeys.includes(e.key) && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
        // Only dismiss if not selecting with Enter/Tab
        if (e.key !== 'Enter' && e.key !== 'Tab') {
          setShowSuggestions(false);
          return false; // Let the key event continue normally
        }
      }
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const item = suggestions[selectedIndex];
        const text = item?.value ?? item?.id ?? (typeof item === 'string' ? item : '');
        insertSuggestion(text, item);
        return true;
      }
      
      return false;
    },
    dismiss: () => {
      setShowSuggestions(false);
    }
  }));

  useEffect(() => {
    if (!query || cursorPosition == null || !textareaRef?.current) {
      setShowSuggestions(false);
      return;
    }

    const { word } = getCurrentWord();
    
    // Hide suggestions if user typed characters that indicate they're continuing to type
    // (spaces, commas, semicolons, etc.) - but allow dots for table.column notation
    const textBefore = query.substring(0, cursorPosition);
    const lastChar = textBefore.slice(-1);
    const dismissChars = [' ', ',', ';', '(', ')', '=', '<', '>', '!', '+', '-', '*', '/'];
    
    // If last character is a dismiss character (and not part of a word with dot), hide suggestions
    if (dismissChars.includes(lastChar) && !word.includes('.')) {
      setShowSuggestions(false);
      return;
    }
    
    const inTableContext = isAfterTableContextKeyword();
    const inColumnContext = isAfterColumnContextKeyword() || isAfterTableOrAliasDot();
    let list = [];

    // Check if we're in a function context (after SELECT, WHERE, HAVING, etc.)
    const isInFunctionContext = () => {
      if (!query || cursorPosition == null) return false;
      const textBefore = query.substring(0, cursorPosition);
      const upperBefore = textBefore.toUpperCase();
      const functionContextKeywords = ['SELECT', 'WHERE', 'HAVING', 'ORDER BY', 'GROUP BY', 'SET', 'VALUES'];
      return functionContextKeywords.some(keyword => {
        const index = upperBefore.lastIndexOf(keyword);
        if (index === -1) return false;
        const afterKeyword = textBefore.substring(index + keyword.length).trim();
        // Check if there's no complete function call between keyword and cursor
        return afterKeyword.length > 0 && !afterKeyword.match(/^[A-Z_][A-Z0-9_]*\s*\(/);
      });
    };

    if (word.length >= 1) {
      const w = word.toUpperCase();
      
      // Add keywords
      const keywordMatches = SQL_KEYWORDS.filter(k => k.toUpperCase().startsWith(w)).slice(0, 10);
      list = keywordMatches.map(k => ({ type: 'keyword', value: k, label: k, description: 'SQL Keyword' }));

      // Add Oracle functions if in function context
      if (isInFunctionContext()) {
        const functionMatches = ORACLE_FUNCTIONS.filter(f => f.toUpperCase().startsWith(w)).slice(0, 15);
        functionMatches.forEach(f => {
          if (!list.find(item => item.value === f)) {
            list.push({ type: 'function', value: f + '(', label: f + '()', description: 'Oracle Function' });
          }
        });
      }

      // Add tables/columns if in table context
      if (inTableContext) {
        const tableNames = tables.map(t => typeof t === 'string' ? t : (t?.id || t?.name || ''));
        const packageNames = Array.isArray(packages) ? packages : [];
        const partial = word.replace(/\./g, '');
        const seen = new Set(list.map(i => i.value));
        [...tableNames, ...packageNames].filter(Boolean).forEach(name => {
          if (name.toLowerCase().startsWith(partial.toLowerCase()) && !seen.has(name)) {
            seen.add(name);
            list.push({ type: 'table', value: name, label: name, description: 'Table/View' });
          }
        });
      }
    } else {
      // Show suggestions even when word is empty
      if (isInFunctionContext()) {
        ORACLE_FUNCTIONS.slice(0, 20).forEach(f => {
          list.push({ type: 'function', value: f + '(', label: f + '()', description: 'Oracle Function' });
        });
      }
    }

    if (inTableContext && (tables.length > 0 || (Array.isArray(packages) && packages.length > 0))) {
      const tableNames = tables.map(t => typeof t === 'string' ? t : (t?.id || t?.name || ''));
      const packageNames = Array.isArray(packages) ? packages : [];
      const seen = new Set();
      [...tableNames, ...packageNames].filter(Boolean).slice(0, 25).forEach(name => {
        if (!seen.has(name)) {
          seen.add(name);
          list.push({ type: 'table', value: name, label: name });
        }
      });
    }

    // Column suggestions (from selected table in Table Browser) - show when typing after SELECT, table., etc.
    if (inColumnContext && columns.length > 0) {
      const colNames = columns.map(c => (typeof c === 'string' ? c : (c?.column_name || c?.name || ''))).filter(Boolean);
      const partial = word.replace(/\./g, '');
      const matches = partial.length >= 1
        ? colNames.filter(c => c.toUpperCase().startsWith(partial.toUpperCase())).slice(0, 20)
        : colNames.slice(0, 25);
      const seen = new Set(list.map(i => i.value));
      if (partial.length < 1) list.unshift({ type: 'column', value: '*', label: '*', description: 'All columns' });
      matches.forEach(name => {
        if (!seen.has(name)) {
          seen.add(name);
          list.push({ type: 'column', value: name, label: name, description: 'Column' });
        }
      });
    }

    if (list.length === 0) {
      setShowSuggestions(false);
      return;
    }

    setSuggestions(list);
    setShowSuggestions(true);
    setSelectedIndex(0);

    const textarea = textareaRef.current;
    const textBeforeCursor = query.substring(0, cursorPosition);
    const lines = textBeforeCursor.split('\n');
    const currentLineIndex = lines.length - 1;
    const lineText = lines[currentLineIndex] || '';
    const measureSpan = document.createElement('span');
    measureSpan.style.visibility = 'hidden';
    measureSpan.style.position = 'absolute';
    measureSpan.style.font = window.getComputedStyle(textarea).font;
    measureSpan.style.whiteSpace = 'pre';
    measureSpan.textContent = lineText;
    document.body.appendChild(measureSpan);
    const textWidth = measureSpan.offsetWidth;
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10) || 20;
    document.body.removeChild(measureSpan);
    const rect = textarea.getBoundingClientRect();
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    setSuggestionPosition({
      top: rect.top + (currentLineIndex + 1) * lineHeight - scrollTop + 5,
      left: rect.left + Math.min(textWidth, textarea.offsetWidth - 100) - scrollLeft + 5
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, cursorPosition, tables, packages, columns, textareaRef]); // getCurrentWord and context helpers are stable

  useEffect(() => {
    if (showSuggestions && suggestionsRef.current) {
      const el = suggestionsRef.current.children[selectedIndex];
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, showSuggestions]);

  // Dismiss on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        const textarea = textareaRef?.current;
        if (textarea && !textarea.contains(event.target)) {
          setShowSuggestions(false);
        }
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions, textareaRef]);

  if (!showSuggestions || suggestions.length === 0) return null;

  return (
    <div
      ref={suggestionsRef}
      className="sql-autocomplete-dropdown"
      style={{
        position: 'fixed',
        top: `${suggestionPosition.top}px`,
        left: `${suggestionPosition.left}px`,
        zIndex: 1000
      }}
      onMouseDown={(e) => e.preventDefault()} // Prevent textarea from losing focus
    >
      <div className="sql-autocomplete-header">
        <span className="sql-autocomplete-hint">Press ESC to dismiss • Click to select</span>
      </div>
      {suggestions.map((item, index) => {
        const value = item?.value ?? item?.id ?? (typeof item === 'string' ? item : '');
        const label = item?.label ?? value;
        const type = item?.type || 'keyword';
        return (
          <div
            key={index}
            className={`sql-autocomplete-item ${index === selectedIndex ? 'selected' : ''} sql-autocomplete-item--${type}`}
            onClick={() => insertSuggestion(value, item)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span className="sql-autocomplete-item-label">{label}</span>
            <span className="sql-autocomplete-item-type">
              {type === 'keyword' ? 'Keyword' : 
               type === 'function' ? 'Function' : 
               type === 'table' ? 'Table' : 
               type === 'column' ? 'Column' : type}
            </span>
            {item.description && (
              <span className="sql-autocomplete-item-description">{item.description}</span>
            )}
          </div>
        );
      })}
    </div>
  );
});

SqlAutocomplete.displayName = 'SqlAutocomplete';

export default SqlAutocomplete;
